import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ApexCharts from 'react-apexcharts';

import { useNotification } from 'config/Notification';

import { fetchRiskAnualLoss as fetchRiskAnualLossService } from 'services/modules/crq/riskquantservice';

import { CircularProgress, Divider } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

const DashboardPage = () => {

  // State variables
  const [annualRiskLoss, setAnnualRiskLoss] = useState([]);
  const [loading, setLoading] = useState(false);

  // Redux selectors
  const companyDataContainer = useSelector((state) => state.auth.companyData);

  // Notification
  const { openNotification } = useNotification();

  useEffect(() => {
    if (companyDataContainer?.companyid !== null) {
      fetchRiskLossData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDataContainer]);

  // Method to fetch the risk loss data based on company id
  const fetchRiskLossData = async () => {
    try {
      setLoading(true);
      let formattedData = [];
      const response = await fetchRiskAnualLossService({ company: companyDataContainer?.companyid });
      if (response.status === 200 || response.status === 201) {
        formattedData = formatData(response.data);
      }
      setAnnualRiskLoss(formattedData);
      setLoading(false);
    } catch (error) {
      openNotification('Server error', 'error');
      setLoading(false);
    }
  };

  // Formats the x and y axis of the graph
  const formatData = (data) => {
    return data.map((e) => {
      return {
        label: e.label,
        maximum_loss: formatDollarValue(e.maximum_loss),
        median: formatDollarValue(e.median),
        ninteenthpercentile: formatDollarValue(e.ninteenthpercentile),
        chartOptions: {
          chart: {
            height: 350,
            type: 'area',
            zoom: {
              enabled: false,
            }
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'smooth',
          },
          xaxis: {
            categories: e.edges.map((edge) => formatEdgeValue(edge)),
          },
          yaxis: {
            max: 100,
            min: 0,
          },
        },
        series: [{
          name: e.label,
          data: e.cumrev,
        }],
      };
    });
  };

  // Formats the loss value
  const formatDollarValue = (value) => {
    const numericValue = parseFloat(value.replace(/[^\d.-]/g, ''));
    if (!isNaN(numericValue)) {
      if (numericValue >= 1000000000) {
        return '$' + (numericValue / 1000000000).toFixed(2) + ' B';
      } else if (numericValue >= 1000000) {
        return '$' + (numericValue / 1000000).toFixed(2) + ' M';
      } else if (numericValue >= 1000) {
        return '$' + (numericValue / 1000).toFixed(2) + ' K';
      } else {
        return '$' + numericValue.toFixed(2);
      }
    }
    return value;
  };

  // Formats the edge value
  const formatEdgeValue = (value) => {
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      if (numericValue >= 1000000000) {
        return '$' + (numericValue / 1000000000).toFixed(2) + ' B';
      } else if (numericValue >= 1000000) {
        return '$' + (numericValue / 1000000).toFixed(2) + ' M';
      } else if (numericValue >= 1000) {
        return '$' + (numericValue / 1000).toFixed(2) + ' K';
      } else {
        return numericValue.toFixed(2);
      }
    }
    return value;
  };

  return (
    <div className="container p-2">
      <h5 className='curve-heading'>Loss Exceedance Curve </h5>
      <Divider className='divider m-3' />
      <div className="flex-container-2">
        {loading ? (
          <div className='card-box'><CircularProgress /></div>
        ) : annualRiskLoss.length === 0 ? (
          <div className="card card-bg Loss-exceedance-container p-4">
            <div className="error-icon">
              <InfoIcon className='crq-info-icon' /> <br />
              <label>No data available</label>
            </div>
            <div className='m-2'>
              <p>Kindly provide risk parameters information to obtain loss exceedance chart</p>
            </div>
          </div>
        ) : (
          <div className='card card-crq-graph'>
            {annualRiskLoss.map((item, index) => (
              <div key={index} className="p-4">
                <div>
                  <h6><b>{item.label}</b></h6>
                </div>
                <div className="row wrapper-row">
                  <div className='losses-card'>
                    <div className="header-labels">
                      <b>Maximum loss</b>
                      <br /> {item.maximum_loss}
                    </div>
                    <div className="header-labels">
                      <b>Median loss</b>
                      <br /> {item.median}
                    </div>
                    <div className="header-labels">
                      <b>Value at risk (90th percentile)</b>
                      <br /> {item.ninteenthpercentile}
                    </div>
                  </div>
                  <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
                    <div id="chart">
                      <ApexCharts type="area" height={350} options={item.chartOptions} series={item.series} />
                    </div>
                  </div>
                </div>
                <hr />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
